import ApplicationController from './application_controller'
import { loadGoogleMaps } from './utils/google_maps_loader'

export default class extends ApplicationController {
  static targets = [
    'map', 'pointCard', 'listView', 'mapView', 'listViewBtn', 'mapViewBtn',
    'mobilePopup', 'popupName', 'popupAddress', 'popupDetails', 'popupSchedule'
  ]
  static values = {
    points: Array,
    center: Object,
    markerIconUrl: String
  }

  async connect() {
    this.selectedPointId = null
    this.markers = []
    this.infoWindows = []
    this.currentView = 'list' // list or map for mobile
    this.mapInitialized = false
    await loadGoogleMaps()
    if (!this.element.isConnected) return

    this.initMobileView()
  }

  initMobileView() {
    // On desktop, show both views and init map
    // On mobile/tablet, start with list view (map will be initialized on first view)
    if (window.innerWidth < 1024) {
      this.switchToListView()
    } else {
      // Desktop: show both with flex layout
      if (this.hasListViewTarget) {
        this.listViewTarget.classList.remove('hidden')
        this.listViewTarget.classList.add('flex', 'flex-col')
      }
      if (this.hasMapViewTarget) {
        this.mapViewTarget.classList.remove('hidden')
        this.mapViewTarget.classList.add('flex', 'flex-col')
      }
      // Initialize map for desktop
      this.initMap()
      this.mapInitialized = true
    }
  }

  initMap() {
    // Initialize the map centered on the delivery address or first point
    const center = this.centerValue || this.getDefaultCenter()

    this.map = new google.maps.Map(this.mapTarget, {
      zoom: 12,
      center: center,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      styles: [
        {
          featureType: 'poi',
          elementType: 'labels',
          stylers: [{ visibility: 'off' }]
        }
      ]
    })

    // Add markers for each relay point
    this.addMarkers()
  }

  getDefaultCenter() {
    if (this.pointsValue && this.pointsValue.length > 0) {
      return {
        lat: parseFloat(this.pointsValue[0].latitude),
        lng: parseFloat(this.pointsValue[0].longitude)
      }
    }
    // Default to Paris if no points
    return { lat: 48.8566, lng: 2.3522 }
  }

  addMarkers() {
    this.pointsValue.forEach((point, index) => {
      const position = {
        lat: parseFloat(point.latitude),
        lng: parseFloat(point.longitude)
      }

      // Create custom marker icon using location pin image
      const marker = new google.maps.Marker({
        position: position,
        map: this.map,
        icon: {
          url: this.markerIconUrlValue,
          scaledSize: new google.maps.Size(32, 32),
          anchor: new google.maps.Point(16, 32)
        },
        pointId: point.identifier
      })

      // Create info window content
      const infoWindow = new google.maps.InfoWindow({
        content: this.createInfoWindowContent(point)
      })

      // Add listener to style InfoWindow after it opens
      google.maps.event.addListener(infoWindow, 'domready', () => {
        this.styleInfoWindow()

        // Add click handler for mobile button
        const mobileBtn = document.querySelector('.mobile-select-point-btn')
        if (mobileBtn) {
          mobileBtn.addEventListener('click', (e) => {
            e.stopPropagation()
            const pointId = e.target.getAttribute('data-point-id')
            this.selectPointAndSubmit(pointId)
          })
        }
      })

      // Add listener to deselect card when InfoWindow is closed
      google.maps.event.addListener(infoWindow, 'closeclick', () => {
        this.deselectCardFromMarker(point.identifier)
      })

      // Add click listener to marker
      marker.addListener('click', () => {
        this.closeAllInfoWindows()
        infoWindow.open(this.map, marker)
        // Auto-select corresponding card in list
        this.selectCardFromMarker(point.identifier)
      })

      this.markers.push(marker)
      this.infoWindows.push(infoWindow)
    })

    // Fit bounds to show all markers
    this.fitBounds()
  }

  createInfoWindowContent(point) {
    const scheduleHtml = this.formatScheduleForInfoWindow(point.opening_hours)
    const distance = this.formatDistance(point.distance_in_meters)
    const time = Math.floor(point.distance_in_meters / 60)
    const isMobileOrTablet = window.innerWidth < 1024

    const buttonHtml = isMobileOrTablet ? `
      <button
        type="button"
        class="mobile-select-point-btn"
        data-point-id="${point.identifier}"
        style="width: 100%; background-color: #2563EB; color: white; font-weight: 500; padding: 10px 16px; border-radius: 8px; border: none; cursor: pointer; margin-top: 8px; font-size: 14px;">
        Me livrer à cette adresse
      </button>
    ` : ''

    return `
      <div class="relay-point-info-scroll" style="width: 300px; box-sizing: border-box; max-height: ${isMobileOrTablet ? '320px' : '350px'}; overflow-y: scroll; overflow-x: hidden; -webkit-overflow-scrolling: touch;">
        <div style="padding: 8px 12px 12px 12px;">
          <h3 style="font-weight: 700; font-size: 17px; color: #111827; margin-bottom: 4px; margin-top: 0; line-height: 1.3;">${this.escapeHtml(point.name)}</h3>
          <p style="font-size: 13px; color: #6B7280; margin-bottom: 8px; margin-top: 0;">${this.escapeHtml(point.address_line1)}, ${this.escapeHtml(point.postal_code)} ${this.escapeHtml(point.city)}</p>

          <div style="display: flex; align-items: center; gap: 6px; font-size: 13px; color: #374151; margin-bottom: 8px; padding-bottom: 8px; border-bottom: 1px solid #E5E7EB;">
            <span style="font-weight: 600;">${point.type || 'Point la poste'}</span>
            <span>•</span>
            <span>${time} min</span>
            <span>•</span>
            <span>${distance}</span>
          </div>

          ${scheduleHtml}
          ${buttonHtml}
        </div>
      </div>
    `
  }

  formatScheduleForInfoWindow(openingHours) {
    if (!openingHours || !Array.isArray(openingHours) || openingHours.length === 0) {
      return '<div style="font-size: 13px; color: #6B7280;">Horaires non disponibles</div>'
    }

    let html = '<div style="margin-top: 0;">'
    openingHours.forEach(schedule => {
      html += `
        <div style="display: flex; justify-content: space-between; font-size: 13px; margin-bottom: 3px;">
          <span style="font-weight: 600; color: #374151;">${this.escapeHtml(schedule.day_name)}</span>
          <span style="color: #6B7280;">${this.escapeHtml(schedule.hours)}</span>
        </div>
      `
    })
    html += '</div>'
    return html
  }

  formatSchedule(openingHours) {
    if (!openingHours || !Array.isArray(openingHours) || openingHours.length === 0) {
      return ''
    }

    let html = '<div class="border-t pt-2 mt-2 text-xs space-y-1">'

    openingHours.forEach(schedule => {
      html += `<div class="flex justify-between"><span class="font-medium">${this.escapeHtml(schedule.day_name)}</span><span>${this.escapeHtml(schedule.hours)}</span></div>`
    })

    html += '</div>'
    return html
  }

  formatDistance(meters) {
    const km = (meters / 1000).toFixed(2)
    return `${km} km`
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  closeAllInfoWindows() {
    this.infoWindows.forEach(infoWindow => infoWindow.close())
  }

  fitBounds() {
    if (this.markers.length === 0) return

    const bounds = new google.maps.LatLngBounds()
    this.markers.forEach(marker => {
      bounds.extend(marker.getPosition())
    })
    this.map.fitBounds(bounds)
  }

  toggleCardExpansion(event) {
    // Prevent event bubbling for radio button clicks
    if (event.target.tagName === 'INPUT') {
      return
    }

    const card = event.currentTarget
    const pointId = card.dataset.pointId
    const scheduleSection = card.querySelector('.card-schedule-section')

    // Close all other cards
    this.pointCardTargets.forEach(otherCard => {
      if (otherCard !== card) {
        const otherSchedule = otherCard.querySelector('.card-schedule-section')

        if (otherSchedule) {
          otherSchedule.classList.add('hidden')
        }

        // Reset border if not selected
        const radio = otherCard.querySelector('input[type="radio"]')
        if (radio && !radio.checked) {
          otherCard.classList.remove('border-blue-500', 'bg-blue-50')
          otherCard.classList.add('border-gray-200')
        }
      }
    })

    // Toggle current card
    if (scheduleSection) {
      const isHidden = scheduleSection.classList.contains('hidden')

      if (isHidden) {
        scheduleSection.classList.remove('hidden')
        // Highlight card
        card.classList.add('border-blue-500', 'bg-blue-50')
        card.classList.remove('border-gray-200')
      } else {
        scheduleSection.classList.add('hidden')
        // Reset border if not selected
        const radio = card.querySelector('input[type="radio"]')
        if (radio && !radio.checked) {
          card.classList.remove('border-blue-500', 'bg-blue-50')
          card.classList.add('border-gray-200')
        }
      }
    }

    // Highlight marker on map
    this.highlightMarkerForCard(pointId)
  }

  confirmPointSelection(event) {
    event.stopPropagation()

    const pointId = event.currentTarget.dataset.pointId
    this.selectedPointId = pointId

    // Enable hidden fields for selected point
    this.enableHiddenFieldsFor(pointId)

    // Check the radio button
    const card = event.currentTarget.closest('.relay-point-card')
    if (card) {
      const radio = card.querySelector('input[type="radio"]')
      if (radio) {
        radio.checked = true
      }

      // Update card styling
      card.classList.add('border-blue-500', 'bg-blue-50')
      card.classList.remove('border-gray-200')
    }

    // Submit the form
    const form = document.getElementById('relay_points_form')
    if (form) {
      form.requestSubmit()
    }
  }

  selectPointAndSubmit(pointId) {
    this.selectedPointId = pointId

    // Enable hidden fields for selected point
    this.enableHiddenFieldsFor(pointId)

    // Find and check the radio button
    const radio = document.getElementById(`point_${pointId}`)
    if (radio) {
      radio.checked = true
    }

    // Submit the form
    const form = document.getElementById('relay_points_form')
    if (form) {
      form.requestSubmit()
    }
  }

  highlightMarkerForCard(pointId) {
    const marker = this.markers.find(m => m.pointId === pointId)
    const infoWindow = this.infoWindows[this.markers.indexOf(marker)]

    if (marker && infoWindow) {
      this.closeAllInfoWindows()
      infoWindow.open(this.map, marker)
      this.map.panTo(marker.getPosition())
      this.map.setZoom(Math.max(this.map.getZoom(), 14))
    }
  }

  enableHiddenFieldsFor(pointId) {
    // Disable all hidden fields first
    document.querySelectorAll('[data-point-fields]').forEach(container => {
      container.querySelectorAll('input').forEach(input => {
        input.disabled = true
      })
    })

    // Enable hidden fields for selected point
    const selectedFields = document.querySelector(`[data-point-fields="${pointId}"]`)
    if (selectedFields) {
      selectedFields.querySelectorAll('input').forEach(input => {
        input.disabled = false
      })
    }
  }


  recenterMap() {
    this.fitBounds()
  }

  // Mobile view toggles
  switchToListView() {
    this.currentView = 'list'

    // Show list, hide map
    if (this.hasListViewTarget) {
      this.listViewTarget.classList.remove('hidden')
      this.listViewTarget.classList.add('flex', 'flex-col')
    }
    if (this.hasMapViewTarget) {
      this.mapViewTarget.classList.add('hidden')
      this.mapViewTarget.classList.remove('flex', 'flex-col')
    }

    // Update button styles - Tab style
    if (this.hasListViewBtnTarget) {
      this.listViewBtnTarget.classList.remove('text-gray-400', 'border-transparent')
      this.listViewBtnTarget.classList.add('text-blue-600', 'border-blue-600')
    }
    if (this.hasMapViewBtnTarget) {
      this.mapViewBtnTarget.classList.remove('text-blue-600', 'border-blue-600')
      this.mapViewBtnTarget.classList.add('text-gray-400', 'border-transparent')
    }
  }

  switchToMapView() {
    this.currentView = 'map'

    // Hide list, show map
    if (this.hasListViewTarget) {
      this.listViewTarget.classList.add('hidden')
      this.listViewTarget.classList.remove('flex', 'flex-col')
    }
    if (this.hasMapViewTarget) {
      this.mapViewTarget.classList.remove('hidden')
      this.mapViewTarget.classList.add('flex', 'flex-col')
    }

    // Update button styles - Tab style
    if (this.hasListViewBtnTarget) {
      this.listViewBtnTarget.classList.remove('text-blue-600', 'border-blue-600')
      this.listViewBtnTarget.classList.add('text-gray-400', 'border-transparent')
    }
    if (this.hasMapViewBtnTarget) {
      this.mapViewBtnTarget.classList.remove('text-gray-400', 'border-transparent')
      this.mapViewBtnTarget.classList.add('text-blue-600', 'border-blue-600')
    }

    // Initialize map if not already done (for mobile first load)
    // Use setTimeout to ensure DOM is fully rendered
    setTimeout(() => {
      if (!this.mapInitialized) {
        this.initMap()
        this.mapInitialized = true
      } else if (this.map) {
        // Trigger map resize to fix rendering issues
        google.maps.event.trigger(this.map, 'resize')
        this.fitBounds()
      }
    }, 100)
  }

  // Mobile popup management
  showMobilePopup(point) {
    if (!this.hasMobilePopupTarget) return

    // Only show on mobile/tablet when in map view
    if (window.innerWidth >= 1024 || this.currentView !== 'map') return

    // Populate popup content
    if (this.hasPopupNameTarget) {
      this.popupNameTarget.textContent = point.name
    }
    if (this.hasPopupAddressTarget) {
      this.popupAddressTarget.textContent = `${point.address_line1}, ${point.postal_code} ${point.city}`
    }
    if (this.hasPopupDetailsTarget) {
      const distance = (point.distance_in_meters / 1000.0).toFixed(2)
      const time = Math.floor(point.distance_in_meters / 60)
      this.popupDetailsTarget.innerHTML = `
        <div class="flex items-center gap-1">
          <span>${point.type || 'Point la poste'}</span>
        </div>
        <div class="flex items-center gap-1">
          <span>${time} min</span>
          <span>·</span>
          <span>${distance} km</span>
        </div>
      `
    }

    // Populate schedule
    if (this.hasPopupScheduleTarget && point.opening_hours && point.opening_hours.length > 0) {
      let scheduleHtml = ''
      point.opening_hours.forEach(schedule => {
        scheduleHtml += `
          <div class="flex justify-between">
            <span class="font-semibold text-gray-700">${schedule.day_name}</span>
            <span class="text-gray-600">${schedule.hours}</span>
          </div>
        `
      })
      this.popupScheduleTarget.innerHTML = scheduleHtml
    } else if (this.hasPopupScheduleTarget) {
      this.popupScheduleTarget.innerHTML = '<span class="text-gray-500 text-xs">Horaires non disponibles</span>'
    }

    // Show popup
    this.mobilePopupTarget.classList.remove('hidden')
  }

  closeMobilePopup() {
    if (this.hasMobilePopupTarget) {
      this.mobilePopupTarget.classList.add('hidden')
    }
  }

  selectCardFromMarker(pointId) {
    // Find the card with matching pointId
    const card = this.pointCardTargets.find(c => c.dataset.pointId === pointId)

    if (!card) return

    // Close all other cards first
    this.pointCardTargets.forEach(otherCard => {
      if (otherCard !== card) {
        const otherSchedule = otherCard.querySelector('.card-schedule-section')
        if (otherSchedule) {
          otherSchedule.classList.add('hidden')
        }
        // Reset border if not selected
        const radio = otherCard.querySelector('input[type="radio"]')
        if (radio && !radio.checked) {
          otherCard.classList.remove('border-blue-500', 'bg-blue-50')
          otherCard.classList.add('border-gray-200')
        }
      }
    })

    // Expand the selected card
    const scheduleSection = card.querySelector('.card-schedule-section')
    if (scheduleSection) {
      scheduleSection.classList.remove('hidden')
      card.classList.add('border-blue-500', 'bg-blue-50')
      card.classList.remove('border-gray-200')
    }

    // Scroll to the card (only on desktop or list view on mobile/tablet)
    if (window.innerWidth >= 1024 || this.currentView === 'list') {
      card.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }

  deselectCardFromMarker(pointId) {
    // Find the card with matching pointId
    const card = this.pointCardTargets.find(c => c.dataset.pointId === pointId)

    if (!card) return

    // Collapse the schedule section
    const scheduleSection = card.querySelector('.card-schedule-section')
    if (scheduleSection) {
      scheduleSection.classList.add('hidden')
    }

    // Reset border style (unless the radio is checked)
    const radio = card.querySelector('input[type="radio"]')
    if (radio && !radio.checked) {
      card.classList.remove('border-blue-500', 'bg-blue-50')
      card.classList.add('border-gray-200')
    }
  }

  styleInfoWindow() {
    // Apply blue border styling to InfoWindow
    const iwContainer = document.querySelector('.gm-style-iw-c')
    const iwContent = document.querySelector('.gm-style-iw-d')
    const iwWrapper = document.querySelector('.gm-style-iw')
    const iwTail = document.querySelector('.gm-style-iw-tc')

    if (iwContainer) {
      iwContainer.style.borderRadius = '16px'
      iwContainer.style.border = '3px solid #3B82F6'
      iwContainer.style.boxShadow = '0 10px 40px rgba(59, 130, 246, 0.3)'
      iwContainer.style.padding = '0'
      iwContainer.style.background = 'white'
      iwContainer.style.maxWidth = '350px'
      iwContainer.style.minWidth = '300px'
      iwContainer.style.maxHeight = '400px'
      iwContainer.style.minHeight = 'auto'
      iwContainer.style.height = 'auto'
      iwContainer.style.overflow = 'visible'
    }

    if (iwContent) {
      iwContent.style.overflow = 'hidden'
      iwContent.style.maxWidth = 'none'
      iwContent.style.maxHeight = '400px'
      iwContent.style.minHeight = 'auto'
      iwContent.style.height = 'auto'
    }

    if (iwWrapper) {
      iwWrapper.style.maxHeight = '400px'
      iwWrapper.style.height = 'auto'
    }

    if (iwTail) {
      // Style the tail/triangle
      const style = document.createElement('style')
      style.textContent = `
        .gm-style-iw-tc::after {
          background: #3B82F6 !important;
          border: 3px solid #3B82F6 !important;
          border-right: none !important;
          border-top: none !important;
          width: 20px !important;
          height: 20px !important;
        }
        .gm-style .gm-style-iw {
          max-height: 400px !important;
          height: auto !important;
        }
        .gm-style .gm-style-iw-c {
          max-width: 350px !important;
          max-height: 400px !important;
          min-height: auto !important;
          height: auto !important;
        }
        .gm-style .gm-style-iw-d {
          overflow: hidden !important;
          max-height: 400px !important;
          min-height: auto !important;
          height: auto !important;
        }
        .gm-style .gm-style-iw-chr {
          display: block !important;
        }
        .relay-point-info-scroll::-webkit-scrollbar {
          width: 8px;
        }
        .relay-point-info-scroll::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        .relay-point-info-scroll::-webkit-scrollbar-thumb {
          background: #888;
          border-radius: 10px;
        }
        .relay-point-info-scroll::-webkit-scrollbar-thumb:hover {
          background: #555;
        }
        .mobile-select-point-btn:hover {
          background-color: #1D4ED8 !important;
        }
        .mobile-select-point-btn:active {
          background-color: #1E40AF !important;
        }
        .gm-style .gm-ui-hover-effect {
          top: -16px !important;
          left: 90% !important;
          right: auto !important;
          opacity: 1 !important;
          width: 32px !important;
          height: 32px !important;
          background-color: white !important;
          border-radius: 50% !important;
          box-shadow: 0 2px 6px rgba(0,0,0,0.3) !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
        }
        .gm-style .gm-ui-hover-effect > span {
          background-color: #1F2937 !important;
          margin: 0 !important;
          width: 16px !important;
          height: 16px !important;
        }
        .gm-style .gm-ui-hover-effect:hover {
          background-color: #f3f4f6 !important;
        }
        .gm-style .gm-ui-hover-effect:hover > span {
          background-color: #374151 !important;
        }
        @media (max-width: 1023px) {
          .gm-style .gm-ui-hover-effect {
            top: -12px !important;
            left: 88% !important;
            width: 28px !important;
            height: 28px !important;
          }
          .gm-style .gm-ui-hover-effect > span {
            width: 14px !important;
            height: 14px !important;
          }
        }
      `
      if (!document.querySelector('#infowindow-style')) {
        style.id = 'infowindow-style'
        document.head.appendChild(style)
      }
    }
  }
}
